/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useState } from 'react';
import { createSearchParams, Link as NavigationLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';

// Component imports
import { Col, Collapse, Divider, List, Row, Typography } from 'antd';

// Misc imports
import { DEFAULT_DATETIME_FORMAT } from '../../../constants/main';
import { NAV_BOT, NAV_ORGANIZATION_INTEGRATIONS } from '../../../constants/navigation';
import { INTEGRATION_CATEGORY, PLUGIN_CATEGORY, INTEGRATIONS } from '../../../constants/integrations';
import { useFetchTicketsQuery } from '../../../store/tickets/ticketsApi';
import { getCurrentQueryState } from '../../../store/utils/getCurrentQueryState';
import replaceUrlParams from '../../../utils/string/replaceUrlParams';

const { Paragraph, Text, Link } = Typography;

const SessionTicketsTab = ({ chatSession }) => {
  const { t } = useTranslation();
  const { orgId } = useParams();
  const tenantId = chatSession?.tenant?.id;

  const [ticketingIntegrations, setTicketingIntegrations] = useState([]);

  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: tickets, isLoading: isLoadingTickets, isFetching: isFetchingTickets } = useFetchTicketsQuery({
    orgId,
    tenantId,
    urlParams: { pageSize: 100, associatedId: chatSession?.id }
  }, { skip: ticketingIntegrations.length === 0 || !orgId || !tenantId || !chatSession?.id });

  useEffect(() => {
    if (bot?.plugins) {
      setTicketingIntegrations(bot.plugins.filter(plugin => plugin.integration.category === INTEGRATION_CATEGORY.TICKETING));
    }
  }, [bot]);

  if (!chatSession) {
    return (
      <Text type="secondary">
        No session data available.
      </Text>
    );
  }

  return (
    <div className="overflow-y-auto pl-3 pr-3 overflow-x-hidden" style={{ maxHeight: 590 }}>
      <Text className={'fw-bold fs-lg'}>{t('Tickets')}</Text>
      {ticketingIntegrations.length > 0 ? (

        <Fragment>
          <Col className="mt-3">
            <Text className="fs-sm">
              {t('Chat Session is linked with the following external Ticketing Platforms:')}
            </Text>
            {ticketingIntegrations.map(({ integration }, index) => (
              <Row
                className={classNames('align-items-center pb-2', { 'pt-5': index === 0, 'pt-2': index > 0 })}
              >
                {INTEGRATIONS.find(i => i.type === integration?.type)?.renderTypeIcon({ className: 'text-primary' })}
                <Text className="ml-2">
                  {`${t('{{providerName}} Integration', { providerName: t(INTEGRATIONS.find(i => i.type === integration?.type)?.type, { context: 'type' }) })}:`}
                </Text>
                <Text className={'ml-2 text-primary'}>
                  <NavigationLink
                    to={`${replaceUrlParams(NAV_ORGANIZATION_INTEGRATIONS, { orgId })}?${createSearchParams({ integrationId: integration.id }).toString()}`}>
                    {integration.name}
                  </NavigationLink>
                </Text>
              </Row>
            ))}
          </Col>

          <Collapse defaultActiveKey={['1']} ghost className="ant-collapse-p-0">
            <Collapse.Panel
              showArrow={false}
              collapsible="icon"
              header={<Divider className="fs-base ant-divider-primary" orientation="left">
                <Text strong>
                  <span className="title">
                    {t('Session\'s Tickets')}
                  </span>
                </Text>
              </Divider>}
              key="1"
            >
              <List
                pagination={{
                  position: 'bottom',
                  align: 'center',
                  pageSize: 4,
                  hideOnSinglePage: true,
                  total: tickets?.total || 0
                }}
                dataSource={tickets ? tickets.items : []}
                renderItem={(ticket) => (
                  <div className="pt-2">
                    <Row className="align-items-center ant-row-no-wrap pb-2">
                      {INTEGRATIONS.find(i => i.type === ticketingIntegrations[0]?.type)?.renderTypeIcon({ className: 'text-primary' })}
                      <Text ellipsis className="ml-2 text-primary">
                        <Link href={ticket.url?.replace(/api\/v2\//, '').replace(/\.json$/, '')} target="_blank" rel="noreferrer">
                          {ticket.name}
                        </Link>
                      </Text>
                    </Row>
                    <Row>
                      <Text className="fs-sm text-muted pr-1 pb-1">
                        {`${t('Ticket id')}:`}
                      </Text>
                      <Text className="fs-sm" copyable>
                        {ticket.id}
                      </Text>
                    </Row>
                    <Row>
                      <Text className="fs-sm text-muted pr-1 pb-1">
                        {`${t('Created at')}:`}
                      </Text>
                      <Text className="fs-sm">
                        {moment(ticket.createdAt).format(DEFAULT_DATETIME_FORMAT)}
                      </Text>
                    </Row>
                    <Row>
                      <Text className="fs-sm text-muted pr-1">
                        {`${t('Description')}:`}
                      </Text>
                      <Paragraph className="fs-sm" ellipsis={{ rows: 2, expandable: true, symbol: t('more') }}>
                        {ticket.description}
                      </Paragraph>
                    </Row>
                  </div>
                )}
                loading={isFetchingTickets || isLoadingTickets}
                locale={{
                  emptyText: <Row>
                    <Text
                      className={'text-muted'}>{t('There aren\'t any tickets associated with this chat session.')}</Text>
                  </Row>
                }}/>
            </Collapse.Panel>
          </Collapse>
        </Fragment>
      ) : (
        <Row className="mt-3">
          <Text className="text-muted">
            {t(`The ticketing add-on has not yet been set up. This feature enables the display of tickets created during chat sessions. To activate it, `)}
            <NavigationLink
              to={replaceUrlParams(NAV_BOT, { tenantId, orgId }) + `?view=plugins&scope=${PLUGIN_CATEGORY.TICKETING}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              {`${t('go to the plugins Ticketing section')}.`}
            </NavigationLink>
          </Text>
        </Row>
      )}
    </div>
  );
};

SessionTicketsTab.propTypes = {
  chatSession: PropTypes.array
};

export default SessionTicketsTab;
